// src/pages/public/Terms.js
import React from "react";
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { COMPANY } from "../../utils/constants";

const LAST_UPDATED = "June 5, 2025";

export default function Terms() {
  return (
    <div style={{ paddingTop: "var(--header-h)" }}>
      <Helmet>
        <title>Terms - Aimble I.T. Solutions</title>
        <meta name="description" content="Review the Terms and Conditions for Aimble I.T. Solutions. Understand our operating guidelines, service agreements, and policies for our digital IT services." />
        <link rel="canonical" href="https://www.aimbletech.com/terms" />
      </Helmet>
      <section style={{ padding: "64px 0 96px" }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="section-label">Legal</div>
            <h1 className="heading-1" style={{ marginBottom: 8 }}>Terms of Service</h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: 48 }}>
              Last updated: {LAST_UPDATED}
            </p>

            <div className="prose">
              <Section title="1. Acceptance of Terms">
                <p>
                  By accessing or using the website at www.aimbletech.com or any services
                  provided by Aimble I.T. Solutions ("AimbleTech", "we", "us"), you agree
                  to be bound by these Terms of Service. If you do not agree, please do
                  not use our services.
                </p>
              </Section>

              <Section title="2. Services">
                <p>AimbleTech provides the following services:</p>
                <ul>
                  <li>Web development and web application development</li>
                  <li>Payment gateway integration and processing (via Maya API)</li>
                  <li>Virtual assistant services</li>
                  <li>Website maintenance and support</li>
                  <li>Walk-in IT services: PC rental, printing, photocopy, photo development, and tech merchandise</li>
                </ul>
                <p>
                  All service scopes, deliverables, timelines, and pricing are defined in
                  separate project agreements or confirmed order receipts. These Terms
                  apply alongside any such agreements.
                </p>
              </Section>

              <Section title="3. Client Accounts">
                <p>
                  To access the client portal, you must create an account. You are
                  responsible for maintaining the confidentiality of your login credentials.
                  You agree to notify us immediately of any unauthorized use of your account.
                </p>
                <p>
                  We reserve the right to suspend or terminate accounts that violate these
                  Terms or are used for fraudulent purposes.
                </p>
              </Section>

              <Section title="4. Payments and Billing">
                <p>
                  All prices are quoted in Philippine Peso (PHP) unless otherwise stated.
                  Official BIR-compliant receipts are issued for all payments.
                </p>
                <p>
                  For one-time projects, payment terms are agreed upon in the project
                  contract (typically 50% downpayment, 50% upon completion). For monthly
                  subscriptions, payment is due at the start of each billing period.
                </p>
                <p>
                  Payments are accepted via Maya QR, Maya Wallet, GCash, Visa, Mastercard,
                  and bank transfer. When AimbleTech processes payments on behalf of clients
                  through our Maya gateway, the applicable fee structure is disclosed at the
                  time of agreement.
                </p>
                <p>
                  Subscriptions not renewed within 15 days of expiry may result in service
                  suspension. Data is retained for 30 days after suspension before deletion.
                </p>
              </Section>

              <Section title="5. Intellectual Property">
                <p>
                  Upon full payment, clients own the final deliverables (website files,
                  source code, designs) created specifically for their project, except for
                  third-party components, libraries, and frameworks that carry their own
                  open-source licenses.
                </p>
                <p>
                  AimbleTech retains the right to display completed work in our portfolio
                  unless the client requests otherwise in writing.
                </p>
                <p>
                  The AimbleTech brand, logo, website design, and marketing materials
                  remain the sole property of Aimble I.T. Solutions.
                </p>
              </Section>

              <Section title="6. Limitation of Liability">
                <p>
                  AimbleTech will not be liable for indirect, incidental, or consequential
                  damages arising from the use of our services. Our total liability for any
                  claim shall not exceed the amount paid by the client for the specific
                  service in the 3 months preceding the claim.
                </p>
                <p>
                  We are not responsible for downtime or data loss caused by third-party
                  infrastructure providers (e.g., Firebase, hosting providers, domain
                  registrars, or payment processors).
                </p>
              </Section>

              <Section title="7. Refund Policy">
                <p>
                  For project-based services, downpayments are non-refundable once work
                  has commenced. Refunds on the remaining balance may be considered if
                  we fail to deliver agreed deliverables within the agreed timeline.
                </p>
                <p>
                  For monthly subscriptions, refunds are not issued for partially used
                  billing periods. You may cancel at any time and service continues until
                  the end of the paid period.
                </p>
              </Section>

              <Section title="8. Prohibited Uses">
                <p>You agree not to use our services to:</p>
                <ul>
                  <li>Violate any Philippine law or international regulations</li>
                  <li>Engage in fraudulent, deceptive, or harmful activities</li>
                  <li>Distribute malware, spam, or illegal content</li>
                  <li>Infringe on the intellectual property rights of others</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                </ul>
              </Section>

              <Section title="9. Governing Law">
                <p>
                  These Terms are governed by the laws of the Republic of the Philippines.
                  Any disputes shall be resolved in the appropriate courts of Sorsogon,
                  Philippines, or through mediation as agreed by both parties.
                </p>
              </Section>

              <Section title="10. Changes to Terms">
                <p>
                  We may update these Terms at any time. Continued use of our services
                  after changes constitutes acceptance of the updated Terms. We will
                  notify registered users of material changes by email.
                </p>
              </Section>

              <Section title="11. Contact">
                <p>
                  Questions about these Terms? Contact us at:
                </p>
                <ul>
                  <li><strong>Email:</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                  <li><strong>Address:</strong> {COMPANY.address}</li>
                  <li><strong>Facebook:</strong> <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer">@aimbleph</a></li>
                </ul>
              </Section>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        .prose h2 { font-size: 1.125rem; font-weight: 600; margin: 2.5rem 0 1rem; color: var(--text); font-family: var(--font-head); }
        .prose p { color: var(--text-muted); line-height: 1.8; margin-bottom: 1rem; }
        .prose ul { padding-left: 1.5rem; margin-bottom: 1rem; }
        .prose li { color: var(--text-muted); line-height: 1.8; margin-bottom: 0.4rem; }
        .prose strong { color: var(--text); font-weight: 600; }
        .prose a { color: var(--accent); }
        .section-divider { border: none; border-top: 1px solid var(--border); margin: 2rem 0; }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: "2.5rem" }}>
      <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem",
        color: "var(--text)", fontFamily: "var(--font-head)" }}>{title}</h2>
      {children}
      <hr className="section-divider" />
    </div>
  );
}
